import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { RadioGroup, RadioGroupItem } from '../ui/radio-group';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Button } from '../ui/button';
import { Ban, AlertTriangle, UserX, Wrench, MoreHorizontal } from 'lucide-react';

const REASONS = [
  { value: 'errore', Icon: AlertTriangle },
  { value: 'cliente', Icon: UserX },
  { value: 'rottura', Icon: Wrench },
  { value: 'altro', Icon: MoreHorizontal },
];

/**
 * Dialog per selezionare il MOTIVO di storno (obbligatorio).
 * onConfirm(reason, note) viene chiamato solo con un motivo valido.
 */
export const StornoReasonDialog = ({ open, onOpenChange, onConfirm, title, description, itemName }) => {
  const { t } = useTranslation();
  const [reason, setReason] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) { setReason(''); setNote(''); setBusy(false); }
  }, [open]);

  const label = (v) => t(`storno.reason_${v}`, {
    defaultValue: { errore: 'Errore', cliente: 'Cliente', rottura: 'Rottura', altro: 'Altro' }[v],
  });

  const valid = reason && (reason !== 'altro' || note.trim().length > 0);

  const confirm = async () => {
    if (!valid) return;
    setBusy(true);
    try {
      await onConfirm(reason, note.trim());
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !busy && onOpenChange(o)}>
      <DialogContent className="max-w-md" data-testid="storno-reason-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Ban className="h-5 w-5 text-destructive" />
            {title || t('storno.title', { defaultValue: 'Motivo dello storno' })}
          </DialogTitle>
          <DialogDescription>
            {description || (itemName
              ? t('storno.descItem', { name: itemName, defaultValue: `Seleziona il motivo per stornare "${itemName}".` })
              : t('storno.desc', { defaultValue: 'Seleziona il motivo dello storno.' }))}
          </DialogDescription>
        </DialogHeader>

        <RadioGroup value={reason} onValueChange={setReason} className="grid grid-cols-2 gap-2">
          {REASONS.map(({ value, Icon }) => (
            <Label
              key={value}
              htmlFor={`storno-${value}`}
              data-testid={`storno-reason-${value}`}
              className={`flex items-center gap-2 rounded-xl border p-3 cursor-pointer transition-colors ${reason === value ? 'border-primary bg-primary/5' : 'border-border hover:bg-muted/50'}`}
            >
              <RadioGroupItem value={value} id={`storno-${value}`} />
              <Icon className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-medium">{label(value)}</span>
            </Label>
          ))}
        </RadioGroup>

        {reason === 'altro' && (
          <div className="space-y-1.5">
            <Label htmlFor="storno-note">{t('storno.noteLabel', { defaultValue: 'Specifica il motivo' })}</Label>
            <Textarea id="storno-note" rows={2} value={note} onChange={(e) => setNote(e.target.value)}
              placeholder={t('storno.notePlaceholder', { defaultValue: 'Descrivi il motivo…' })} data-testid="storno-note-input" />
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy} data-testid="storno-cancel">
            {t('common.cancel', { defaultValue: 'Annulla' })}
          </Button>
          <Button variant="destructive" onClick={confirm} disabled={!valid || busy} data-testid="storno-confirm">
            {busy ? t('storno.confirming', { defaultValue: 'Storno…' }) : t('storno.confirm', { defaultValue: 'Conferma storno' })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
